import React from 'react'
import { Avatar, Box, Chip, Divider, Paper, Stack, Typography } from '@mui/material'
import { EmojiEvents, Receipt, Star } from '@mui/icons-material'
import { useCart } from '../../contexts/CartContext'
import type { Cupom } from '../../types/cupons'
import { parseBigDecimal } from '../../utils/bigDecimal'

interface CheckoutSummaryProps {
  subtotal: number
  discount: number
  total: number
  coupon?: Cupom | null
}

const CheckoutSummary: React.FC<CheckoutSummaryProps> = ({ subtotal, discount, total, coupon }) => {
  const { state } = useCart()

  const formatPrice = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

  const couponLimiteValorAplicacao =
    coupon?.tipoDesconto === 'PERCENTUAL' ? parseBigDecimal(coupon.limiteValorAplicacao) : null

  return (
    <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Receipt sx={{ mr: 1.5, color: 'primary.main' }} />
        <Typography variant="h6" fontWeight={900}>
          Resumo
        </Typography>
      </Box>

      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Itens ({state.totalItems})
          </Typography>
          <Stack spacing={1.25}>
            {state.items.map((item) => (
              <Box
                key={item.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.10)'
                }}
              >
                <Avatar variant="rounded" src={item.pacote.urlImagem ?? undefined} alt={item.pacote.nome} sx={{ width: 40, height: 40, borderRadius: 1.5 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={900} sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.pacote.nome}
                    </Typography>
                    {item.pacote.vip && (
                      <Chip icon={<Star sx={{ fontSize: 12 }} />} label="VIP" size="small" color="warning" sx={{ height: 18, fontSize: 11 }} />
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip label={`${item.quantity}x`} size="small" sx={{ bgcolor: 'rgba(0,230,118,0.18)', color: '#00e676', fontWeight: 900 }} />
                    <Typography variant="caption" color="text.secondary">
                      {formatPrice(item.pacote.preco)} cada
                    </Typography>
                    {item.pacote.pontos > 0 && (
                      <Chip icon={<EmojiEvents sx={{ fontSize: 12 }} />} label={`${item.pacote.pontos} pts`} size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />
                    )}
                  </Stack>
                </Box>
                <Typography variant="body2" fontWeight={900} color="primary">
                  {formatPrice(item.pacote.preco * item.quantity)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Divider />

        <Box>
          <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Subtotal
            </Typography>
            <Typography variant="body2" fontWeight={900}>
              {formatPrice(subtotal)}
            </Typography>
          </Stack>

          {discount > 0 && (
            <>
              <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">
                  Desconto{coupon?.codigo ? ` (${coupon.codigo})` : ''}
                </Typography>
                <Typography variant="body2" fontWeight={900} color="success.main">
                  -{formatPrice(discount)}
                </Typography>
              </Stack>
              {couponLimiteValorAplicacao != null && couponLimiteValorAplicacao > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Percentual aplicado até {formatPrice(couponLimiteValorAplicacao)}.
                </Typography>
              )}
            </>
          )}

          <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Taxas
            </Typography>
            <Typography variant="body2" fontWeight={900}>
              R$ 0,00
            </Typography>
          </Stack>

          <Divider sx={{ my: 1.25 }} />

          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Typography variant="h6" fontWeight={900}>
              Total
            </Typography>
            <Typography variant="h6" fontWeight={900} color="primary">
              {formatPrice(total)}
            </Typography>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  )
}

export default CheckoutSummary

